import { GhostButton } from '../../../../../widgets/buttons';
import { Icon } from '../../../../../widgets/icon';
import { Input, SearchInput } from '../../../../../widgets/input';
import { ZTabs } from '../../../../../widgets/ztabs';
import styled, { css } from 'styled-components';

export const ComposerPanel = styled.section`
    display: flex;
    min-width: 0;
    min-height: 0;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
    color: var(--composer-fg, #1e2a38);
    background: var(--composer-panel, #fff);
    border-radius: 12px;
    box-shadow: var(--composer-shadow, rgba(30, 42, 56, 0.05) 0 1px 2px, rgba(30, 42, 56, 0.06) 0 0 0 1px);
`;

export const ComposerPanelHeader = styled.header`
    display: flex;
    min-height: 38px;
    flex: 0 0 auto;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 0 8px;
    border-bottom: 1px solid var(--composer-line-soft, #eaeff4);
`;

export const ComposerPanelTitle = styled.div`
    min-width: 0;
`;

export const ComposerEyebrow = styled.div`
    overflow: hidden;
    color: var(--composer-faint, #677078);
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.08em;
    line-height: 16px;
    text-overflow: ellipsis;
    text-transform: uppercase;
    white-space: nowrap;
`;

export const ComposerTitle = styled.div`
    overflow: hidden;
    color: var(--composer-fg, #1e2a38);
    font-size: 13.5px;
    font-weight: 600;
    line-height: 20px;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const ComposerToolbar = styled.div`
    display: flex;
    min-height: 38px;
    flex: 0 0 auto;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
    padding: 4px 8px;
    background: var(--composer-sunken, #f4f7fa);
    border-bottom: 1px solid var(--composer-line, #dce3eb);
`;

export const ComposerToolbarSpacer = styled.span`
    flex: 1;
`;

/* Only the pressed state is the Composer's own: the resting colour and the hover are GhostButton's. */
export const ComposerIconButton = styled(GhostButton).attrs({ $size: 'small', $type: 'icon' })<{ $active?: boolean; $danger?: boolean }>`
    ${(props) => props.$danger && `color: ${props.theme.app.component.textButton.destructive.default.color};`}

    ${(props) =>
        props.$active &&
        css`
            color: var(--composer-accent-text, #a8480b);
            background: var(--composer-accent-wash, #fdf0e6);
        `}

    ${Icon},
    > svg {
        width: 17px;
        height: 17px;
    }
`;

/* Every row command slot is the shared ghost button at row density: the resting colour, the hover
   and the danger colour are the component's own, and only the pressed wash is the Composer's. */
export const ComposerRowActionButton = styled(GhostButton).attrs({ $size: 'small', $type: 'icon' })<{
    $active?: boolean;
    $danger?: boolean;
    $off?: boolean;
}>`
    width: 28px;
    min-width: 28px;
    height: 28px;
    min-height: 28px;
    border-radius: 5px;

    ${(props) =>
        props.$danger &&
        css`
            color: ${props.theme.app.component.textButton.destructive.default.color};

            &:hover:not([disabled]),
            &:active:not([disabled]) {
                color: ${props.theme.app.component.textButton.destructive.hover.color};
            }
        `}

    ${(props) =>
        (props.$active || props.$off) &&
        css`
            color: var(--composer-accent-text, #a8480b);

            &:hover:not([disabled]),
            &:active:not([disabled]) {
                color: var(--composer-accent-text, #a8480b);
            }
        `}

    ${Icon},
    > svg {
        width: 17px;
        height: 17px;
        fill: currentColor;
    }
`;

/* SearchInput carries the magnifier and hands this class to the field it wraps, not to its own
   container: the container is already a full-width block, so the row needs no flex rules of its own. */
export const ComposerSearch = styled(SearchInput)`
    height: 30px;
`;

export const ComposerSearchRow = styled.div`
    flex: 0 0 auto;
    padding: 6px 8px;
    border-bottom: 1px solid var(--composer-line-soft, #eaeff4);
`;

export const ComposerTreeToolsRow = styled.div`
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: flex-end;
    gap: 2px;
    padding: 4px 8px;
    border-bottom: 1px solid var(--composer-line-soft, #eaeff4);
`;

export const ComposerLinkButton = styled(GhostButton).attrs({ $size: 'small' })`
    width: auto;
    min-width: 0;
    height: auto;
    min-height: 26px;
    padding-inline: 6px;
    border-radius: 6px;
    color: var(--composer-muted, #5c6b7a);
    font-size: 11.5px;

    &:hover:not([disabled]) {
        background-color: transparent;
        color: var(--composer-fg, #1e2a38);
    }

    &:active:not([disabled]) {
        background-color: transparent;
        color: var(--composer-fg, #1e2a38);
    }

    > svg {
        width: 14px;
        height: 14px;
        flex: 0 0 14px;
    }
`;

export const ComposerScrollBody = styled.div`
    min-width: 0;
    min-height: 0;
    flex: 1;
    overflow: auto;
    padding: 8px 6px;
`;

export const ComposerList = styled.div`
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 4px;
`;

export const ComposerSubjectHeader = styled.div`
    display: grid;
    min-height: 66px;
    flex: 0 0 auto;
    grid-template-columns: 30px minmax(0, 1fr);
    align-items: center;
    gap: 10px;
    padding: 8px;
    border-bottom: 1px solid var(--composer-line-soft, #eaeff4);
`;

export const ComposerSubjectMark = styled.div<{ $kind?: 'group' | 'attribute' | 'option' | 'neutral' }>`
    display: flex;
    width: 30px;
    height: 30px;
    align-items: center;
    justify-content: center;
    color: ${(props) => {
        if (props.$kind === 'group') return 'var(--composer-kind-group, #7040d0)';
        if (props.$kind === 'option') return 'var(--composer-kind-option, #b32d7d)';
        if (props.$kind === 'attribute') return 'var(--composer-kind-attribute, #0e7c7c)';
        return 'var(--composer-muted, #5c6b7a)';
    }};
    background: var(--composer-sunken, #f4f7fa);
    border-radius: 5px;

    ${Icon},
    > svg,
    > img {
        width: 20px;
        height: 20px;
        object-fit: cover;
    }
`;

export const ComposerSubjectContent = styled.div`
    min-width: 0;
`;

export const ComposerSubjectName = styled.div`
    overflow: hidden;
    color: var(--composer-fg, #1e2a38);
    font-size: 13.5px;
    font-weight: 600;
    line-height: 18px;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const ComposerSubjectNameInput = styled(Input)`
    width: 100%;
    height: 32px;
    padding: 4px 7px;
    color: ${(props) => props.theme.app.textColor};
    font-size: 15px;
    font-weight: 600;
`;

export const ComposerSubjectMeta = styled.div`
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 6px;
    margin-top: 4px;
    color: var(--composer-faint, #677078);
    font-size: 10.5px;
`;

export const ComposerKindBadge = styled.span<{ $kind?: 'group' | 'attribute' | 'option' | 'neutral' }>`
    display: inline-flex;
    align-items: center;
    padding: 2px 7px;
    color: ${(props) => {
        if (props.$kind === 'group') return 'var(--composer-kind-group, #7040d0)';
        if (props.$kind === 'option') return 'var(--composer-kind-option, #b32d7d)';
        if (props.$kind === 'attribute') return 'var(--composer-kind-attribute, #0e7c7c)';
        return 'var(--composer-muted, #5c6b7a)';
    }};
    background: var(--composer-panel, #fff);
    border: 1px solid currentColor;
    border-radius: 999px;
    font-size: 10px;
    font-weight: 600;
    line-height: 16px;
    text-transform: uppercase;
`;

export const ComposerTabs = styled(ZTabs)`
    display: flex;
    min-width: 0;
    min-height: 0;
    flex: 1;
    flex-direction: column;

    .react-tabs__tab-list {
        width: 100%;
        min-height: 38px;
        margin: 0;
        padding: 6px 8px;
        overflow-x: auto;
        background: var(--composer-sunken, #f4f7fa);
        border-bottom: 1px solid var(--composer-line, #dce3eb);
        border-radius: 0;
    }

    .react-tabs__tab {
        flex: 0 0 auto;
        height: 30px;
        padding: 0 10px;
        color: var(--composer-muted, #5c6b7a);
        background: transparent;
        border-radius: 8px;
        box-shadow: none;
        font-size: 11px;
        font-weight: 600;
    }

    .react-tabs__tab:hover {
        color: var(--composer-fg, #1e2a38);
        background: var(--composer-hover, #e9eff5);
    }

    .react-tabs__tab--selected,
    .react-tabs__tab--selected:hover {
        color: var(--composer-accent-text, #a8480b);
        background: rgb(0 0 0 / 6%);
        box-shadow: none;
    }

    .react-tabs__tab-panel {
        min-width: 0;
        min-height: 0;
        flex: 1;
        overflow: hidden;
    }
`;

export const ComposerTabCount = styled.span`
    display: inline-flex;
    min-width: 18px;
    height: 18px;
    align-items: center;
    justify-content: center;
    padding: 0 5px;
    color: var(--composer-faint, #677078);
    background: var(--composer-panel, #fff);
    border: 1px solid var(--composer-line, #dce3eb);
    border-radius: 999px;
    font-size: 10px;
`;

export const ComposerEmptyState = styled.div`
    display: flex;
    height: 100%;
    min-height: 180px;
    align-items: center;
    justify-content: center;
    padding: 24px;
    color: var(--composer-faint, #677078);
    font-size: 13px;
    line-height: 20px;
    text-align: center;
`;

export const ComposerDetailsStack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

export const ComposerFormStack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export const ComposerDetailCard = styled.div`
    padding: 12px;
    background: var(--composer-panel, #fff);
    border: 1px solid var(--composer-line, #dce3eb);
    border-radius: ${(props) => props.theme.app.component.card.borderRadius};
`;

export const ComposerDetailLabel = styled.div`
    margin-bottom: 4px;
    color: var(--composer-faint, #677078);
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
`;

export const ComposerDetailValue = styled.div`
    overflow-wrap: anywhere;
    color: var(--composer-fg, #1e2a38);
    font-size: 13px;
    line-height: 19px;
`;

export const ComposerUnsupportedNote = styled.div`
    padding: 10px 12px;
    color: var(--composer-muted, #5c6b7a);
    background: var(--composer-sunken, #f4f7fa);
    border: 1px solid var(--composer-line, #dce3eb);
    border-radius: ${(props) => props.theme.app.component.card.borderRadius};
    font-size: 12px;
    line-height: 18px;
`;

export const ComposerResizeHandle = styled.div`
    position: relative;
    width: 6px;
    min-width: 6px;
    height: 100%;
    cursor: col-resize;
    background: var(--composer-app, #eff3f7);

    &::after {
        position: absolute;
        inset: 0 2px;
        background: transparent;
        content: '';
    }

    &:hover::after {
        background: var(--composer-accent, #e8650a);
    }
`;
